import { useState, useEffect } from 'react'
import { getDocument, GlobalWorkerOptions } from 'pdfjs-dist'
import styled from 'styled-components'
import { AttachmentIcon, Wrapper } from '@farewill/ui'
import { COLOR, GTR, FONT } from '@farewill/ui/tokens'
import type { PreviewFile } from './types'

// Configure PDF.js worker (required before getDocument). Use CDN so we don't need to copy worker into public.
const PDFJS_VERSION = '5.4.624'
if (typeof window !== 'undefined') {
  GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${PDFJS_VERSION}/build/pdf.worker.min.mjs`
}

const THUMBNAIL_HEIGHT = 140

const StyledPdfPreview = styled(Wrapper)`
  display: flex;
  flex-direction: column;
  align-items: start;
`

const StyledThumbnail = styled.img`
  width: 100%;
  max-height: ${THUMBNAIL_HEIGHT}px;
  object-fit: contain;
  object-position: bottom left;
  filter: drop-shadow(1px 1px 6px rgba(0, 0, 0, 0.25));
`

const StyledThumbnailPlaceholder = styled(Wrapper)`
  height: ${THUMBNAIL_HEIGHT}px;
  width: 100px;
  background: ${COLOR.BACKGROUND.SMOKE};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${FONT.SIZE.XXS};
  color: ${COLOR.GREY.DARK};
`

const StyledPageCount = styled(Wrapper)`
  font-size: ${FONT.SIZE.XS};
  color: ${COLOR.GREY.DARK};
  margin-top: ${GTR.XS};
  display: flex;
  align-items: center;
  gap: ${GTR.XXS};
`

const StyledFallbackIcon = styled(Wrapper)`
  display: flex;
  align-items: center;
  justify-content: center;
`

type PdfPreviewProps = {
  file: PreviewFile
  footer?: React.ReactNode
}

export const PdfPreview = ({ file, footer }: PdfPreviewProps) => {
  const [thumbnail, setThumbnail] = useState<string | null>(null)
  const [pageCount, setPageCount] = useState<number | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)

  useEffect(() => {
    if (file.type !== 'application/pdf' || !file.preview) {
      setLoading(false)
      return
    }

    let cancelled = false
    setLoading(true)
    setError(false)
    setThumbnail(null)
    setPageCount(null)

    const load = async () => {
      try {
        const loadingTask = getDocument({ url: file.preview })
        const pdf = await loadingTask.promise
        if (cancelled) return

        const numPages = pdf.numPages
        setPageCount(numPages)

        const page = await pdf.getPage(1)
        if (cancelled) return

        const viewport = page.getViewport({ scale: 1 })
        const HIGH_RES_SCALE = 3
        const scale = (THUMBNAIL_HEIGHT / viewport.height) * HIGH_RES_SCALE
        const scaledViewport = page.getViewport({ scale })
        const canvas = document.createElement('canvas')
        const context = canvas.getContext('2d')
        if (!context) {
          setError(true)
          setLoading(false)
          return
        }
        canvas.height = scaledViewport.height
        canvas.width = scaledViewport.width
        await page.render({
          canvas,
          canvasContext: context,
          viewport: scaledViewport,
        }).promise
        if (cancelled) return
        setThumbnail(canvas.toDataURL('image/jpeg'))
      } catch {
        if (!cancelled) setError(true)
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    load()
    return () => {
      cancelled = true
    }
  }, [file.preview, file.type])

  if (error) {
    return (
      <StyledThumbnailPlaceholder>
        <StyledFallbackIcon>
          <AttachmentIcon />
        </StyledFallbackIcon>
      </StyledThumbnailPlaceholder>
    )
  }

  if (loading) {
    return <StyledThumbnailPlaceholder>Loading…</StyledThumbnailPlaceholder>
  }

  return (
    <StyledPdfPreview>
      {thumbnail ? (
        <StyledThumbnail src={thumbnail} alt={file.name} />
      ) : (
        <StyledThumbnailPlaceholder>No preview</StyledThumbnailPlaceholder>
      )}
      {footer}
      {pageCount != null && (
        <StyledPageCount>
          {pageCount} {pageCount === 1 ? 'page' : 'pages'}
        </StyledPageCount>
      )}
    </StyledPdfPreview>
  )
}
